import { MenuType } from "@multiweb/types/multiweb";
import { AwaitableStandardEvent, Event } from "Global/Base/Events";
import { IItemRightsCollection } from "Global/Base/Rights/RightTypes";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { IRightConditionItem } from "OW.Remoting/Remoting/Rights";
import { IFormRight } from "UI/UI/Rights/FormRight";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { SidePanel } from "../Controls/SidePanel";
import { FormManager } from "../FormManager/IFormManager";
import { IFormUI } from "../FormUI/FormUI";
import { UIManager } from "../UIManager";
import { FormContext, IMainMenuFormActionData, IMainMenuItemActionHandler, IMainMenuItemActionParams, IMainMenuItemActionResult, IWindowable, IWindowableParameters, MainMenuItemActionType, WindowState } from "../UIManagerInterfaces";
import { OpenModeType } from "../XGControls/Actions/DetailFormAction";
import { FilterPanel } from "../XGControls/Filters/FilterPanel";
import { FormResult, FormResultType } from "../XGControls/Forms/FormResult";
import { IFormProfileInfo } from "../XGControls/Forms/IFormProfileInfo";
import { IFormProfileManager } from "../XGControls/Forms/IFormProfileManager";
import { IHideButtons } from "../XGControls/Frames";
import { IMasterableFrame } from "../XGControls/Frames/FrameInterfaces";
import { IDetailFrame } from "../XGControls/Frames/IDetailMasterableFrame";
import { FrameRefreshType, IForm, IFormClosingCancelledEventArgs, IFormSavingParameters } from "../XGControls/IForm";
import { IFrame } from "../XGControls/IFrame";
import { ModalWindowType } from "../XGControls/ModalWindow";
import { IFormBuilder } from "./IFormBuilder";

// TODO Presunout do samples vcetne registrace handleru di.register(IMainMenuItemActionHandler, CustomActionHandler, true);
/**
 * Sestaví modální formulář pro graf
 */
export class CustomFormTestBuilder implements IFormBuilder {
    /**
     * Sestaví modální formulář pro ChartFrame
     * @param parametricData Volitelné parametry formuláře
     */
    public async build(usedIform: boolean = false, addToMenu: boolean = true): Promise<IWindowable> {
        const el = document.createElement("div");
        const w = usedIform ? new CustomIFormForm(el) : new CustomForm(el);
        w.titleLocKey = "GUI-1500"; //Graf

        await FormManager.instance.openCustomForm(w, addToMenu);

        await this._createForm(w);

        return w;
    }

    private async _createForm(ownerForm: IWindowable): Promise<void> {
        const element = createElement({ classes: "custom-form-open-test" });
        const delement = document.createElement("div");

        delement.insertAdjacentHTML("afterbegin", "<div>Testovaci vlastni formular</div>");

        const btn1 = createElement({ type: "button", classes: "btn btn-large primary", html: "Otevřít podřizený" });

        if (ownerForm instanceof IForm) {
            btn1.addEventListener("click", () => {
                FormManager.instance.openFormModal({ formName: "FRM.CMerna_jednotka.Edit", showBreadcrumbNav: true, type: ModalWindowType.parametricForm });
            });

            delement.appendChild(btn1);
        }

        element.appendChild(delement);

        ownerForm.getElement().appendChild(element);
    }
}

class CustomForm implements IWindowable {
    private readonly _element: HTMLElement;
    private _closeDisabled: boolean = false;
    private _isActive: boolean = true;
    public titleLocKey?: string;
    public title?: string;
    public subtitle: string;
    public isEdited: boolean;
    public stateChanged: Event<IWindowable, { state: WindowState; value: any }> = new Event;
    public closed: Event<IWindowable, FormResult> = new Event;
    public closing: Event<IWindowable, boolean> = new Event;
    protected _formVisible = false;
    protected _realVisibility = false;
    public isFormVisible = this._formVisible && this._realVisibility;
    public setParametricData(_parametricData: IWindowableParameters): void {
        //throw new Error("Method not implemented.");
    }
    public setState(state: WindowState, value: any): void {
        switch (state) {
            case WindowState.active:
                if (this._isActive != value) {
                    this._isActive = value;
                } else {
                    return;
                }
                break;
            case WindowState.closeDisabled:
                if (this._closeDisabled != value) {
                    this._closeDisabled = value;
                } else {
                    return;
                }
                break;
            case WindowState.realVisibility: {
                this._realVisibility = value;
                break;
            }
            case WindowState.visible: {
                this._formVisible = value;
                break;
            }
        }
        this.stateChanged.trigger(this, { state, value });
    }
    public getState(state: WindowState): boolean {
        switch (state) {
            case WindowState.active:
                return this._isActive;
            case WindowState.closeDisabled:
                return this._closeDisabled;
        }

        return false;
    }
    public getElement(): HTMLElement {
        return this._element;
    }
    public async close(): Promise<boolean> {
        //this._element.remove();
        this.closing.trigger(this, true);
        this.closed.trigger(this, { resultType: FormResultType.ok, resultValue: null });
        return true;
    }
    public doClose(_windowableSender: IWindowable, _result: FormResult): void {
        // Prázdná metoda
    }

    constructor(element: HTMLElement) {
        this._element = element;
    }
}

class CustomIFormForm extends CustomForm implements IForm {
    public element: HTMLElement;
    public profileManager: IFormProfileManager;
    public formContext: FormContext;
    public multiEditMode: boolean;
    public returnMainInstance?: boolean;
    public isImplicit: boolean;
    public originalRightsDefinition: IItemRightsCollection;
    public formName: string = "";
    public formInstanceId: string = "";
    public isDetailForm: boolean = false;
    public version?: string = "";
    public locKey: string = "";
    public profileId: string = "";
    public formUI: IFormUI = null;
    public filterPanel: FilterPanel = null;
    public formSettingsPanel: SidePanel = null;
    public showDetail: boolean = false;
    public setAttributesOnNewObject: string = "";
    public viewMode: boolean = false;
    public isPreviewMode?: boolean = false;
    public visibleOnlyDetail: boolean = false;
    public hideWindowActions: boolean = false;
    public detailFrame: IDetailFrame = null;
    public openInEdit: boolean = false;
    public readOnly: boolean = false;
    public canClose: boolean = true;
    public canNew: boolean = false;
    public canDelete: boolean = false;
    public canRead: boolean = false;
    public lovFrame: string = undefined;
    public canInsertAtFormLevel: boolean = false;
    public openMode: OpenModeType = OpenModeType.standard;
    public purposeType?: MenuType = MenuType.mtForm;
    public readVersioned: boolean = false;
    public insertObjectName?: string = "";
    public masterableFrames: readonly IMasterableFrame[] = [];
    public frames: readonly IFrame[] = [];
    public profileInfo: IFormProfileInfo = null;
    public formRights: IFormRight[] = [];
    public conditionItems: IRightConditionItem[] = [];
    public usedConditionItemsId: string[] = [];
    public initialFormProfileId: number = 0;
    public detailFormMasterFrame?: IMasterableFrame = null;
    public detailFormObject?: OWObject = null;
    public masterObject?: OWObject = null;
    public formCondition?: string = "";
    public formConditionParamAttrs?: readonly string[] = [];
    public formConditionParamsObject?: OWObject = null;
    public masterLinkToAttr?: string = null;
    public selectedId?: string = null;
    public autoSelectFirstRow?: boolean = false;
    public autoSelectOnlyIfOneRow?: boolean = false;
    public isLov?: boolean = false;
    public enableLovSelect?: boolean = false;
    public isModal?: boolean = false;
    public customTitle?: string = "";
    public disableFormMenu?: boolean = false;
    public frameRefreshType?: FrameRefreshType = FrameRefreshType.none;
    public queryMode?: boolean = false;
    public frameToRefreshAfterSetFormActive?: IFrame = null;
    public horizontalLayout?: boolean = false;
    public setValues: string = "";
    public hideButtons?: IHideButtons = null;
    public titleChanged: Event<IForm, string> = new Event;
    public childOpened: Event<IWindowable, void> = new Event;
    public childClosed: Event<IWindowable, void> = new Event;
    public onDoOpen: Event<IWindowable, void> = new Event;
    public readOnlyChanged: Event<IForm, boolean> = new Event;
    public masterObjectChanged: Event<IForm, OWObject> = new Event;
    public framesInitialized: Event<IForm, void> = new Event;
    public closingPreview: AwaitableStandardEvent<IWindowable, void> = new AwaitableStandardEvent;
    public closingCancelled: Event<IWindowable, IFormClosingCancelledEventArgs> = new Event;
    public async init(formUI: IFormUI): Promise<void> {
        //throw new Error("Method not implemented.");
        this.formUI = formUI;
        this.formUI.createFormMenu();
    }
    public initReduxState(): void {
        //throw new Error("Method not implemented.");
    }
    public async actualizeForm(): Promise<void> {
        //throw new Error("Method not implemented.");
    }
    public setVisibleFrame(frame: IFrame, addToUiHistory?: boolean): void;
    public setVisibleFrame(frame: string, addToUiHistory?: boolean): void;
    public setVisibleFrame(_frame: any, _addToUiHistory?: any): void {
        //throw new Error("Method not implemented.");
    }
    public async checkAndSaveForm(_param?: IFormSavingParameters): Promise<boolean> {
        //throw new Error("Method not implemented.");
        return true;
    }
    public async setToolbarToEditMode(_inEdit: boolean): Promise<void> {
        //throw new Error("Method not implemented.");
    }
    public async waitForBussyFrameAndChildren(_sourceFrame: IFrame): Promise<void> {
        //throw new Error("Method not implemented.");
    }
    public clearProfileInfo(): void {
        //throw new Error("Method not implemented.");
    }
    public addSidePanel(_panel: HTMLElement): void {
        //throw new Error("Method not implemented.");
    }
    public dispose(): void {
        //throw new Error("Method not implemented.");
    }
    public ownerForm: IForm;
}

export class CustomActionHandler implements IMainMenuItemActionHandler {
    private ALLOW_DEFAULT = { preventDefault: false };
    private PREVENT_DEFAULT = { preventDefault: true };
    private SPECIAL_FORM_CODE = "FRM.Testovaci_FRM.DashBoard";

    constructor() {
        this.handleAction = this.handleAction.bind(this);
    }

    public handleAction(menuItemParams: IMainMenuItemActionParams): Promise<IMainMenuItemActionResult> {
        const { itemType, itemData } = menuItemParams;
        switch (itemType) {
            case MainMenuItemActionType.form:
                return this.handleFormItem(itemData as IMainMenuFormActionData);
            default:
                return Promise.resolve(this.ALLOW_DEFAULT);
        }
    }

    /**
     * Pro formulář se speciálním kódem provede vlastní chování a potlačí výchozí chování. Pro ostatní formuláře zachová výchozí chování.
     * Řeší zobrazení loading indikátoru v případě potlačeného výchozího chování.
     * @param formData
     */
    private async handleFormItem(formData: IMainMenuFormActionData): Promise<IMainMenuItemActionResult> {
        try {
            UIManager.instance.showLoader();
            if (formData.formCode === this.SPECIAL_FORM_CODE) {
                // Vlastní implementace akce pro speciální formulář.
                const w = new CustomFormTestBuilder();

                await w.build(true, false);

                return this.PREVENT_DEFAULT;
            } else {
                return this.ALLOW_DEFAULT;
            }
        } finally {
            UIManager.instance.hideLoader();
        }
    }
}
